import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect, useState, useCallback, useRef, useMemo } from "react";
import { useSelector, useDispatch } from "react-redux";
import { Box, Paper } from "@mui/material";
import PageContainer from "../../common_components/PageContainer";
import Pagination from "../../common_components/Pagination";
import { fetchModules, createModule, updateModule, deleteModule, selectAllModules, selectModulesLoading, } from "../../redux/moduleSlice";
import ModuleMap from "./Component/ModuleMap";
import ModuleListHeader from "./Component/ModuleListHeader";
import AddEditModuleDrawer from "./AddEditDrawer/AddEditModuleDrawer";
// Constants
const MODULE_PAGINATION = {
    DEFAULT_PAGE: 1,
    LIMIT: 5,
};
// Client-side filter sort
const applyClientSideOperations = (data, sortConfig, searchText) => {
    const search = searchText?.trim().toLowerCase();
    const matchesSearch = (row) => {
        if (!search)
            return true;
        return (row.module_name?.toLowerCase().includes(search) ||
            row.key?.toLowerCase().includes(search) ||
            row.url?.toLowerCase().includes(search));
    };
    const process = (rows) => {
        let result = [];
        for (const row of rows) {
            const children = row.sub_modules?.length ? process(row.sub_modules) : [];
            if (matchesSearch(row) || children.length > 0) {
                result.push({ ...row, sub_modules: children });
            }
        }
        if (sortConfig?.columnId && sortConfig.direction) {
            const { columnId, direction } = sortConfig;
            result.sort((a, b) => {
                const av = a[columnId];
                const bv = b[columnId];
                if (av == null)
                    return 1;
                if (bv == null)
                    return -1;
                if (typeof av === "string")
                    return direction === "asc" ? av.localeCompare(bv) : bv.localeCompare(av);
                if (typeof av === "number")
                    return direction === "asc" ? av - bv : bv - av;
                return 0;
            });
        }
        return result;
    };
    return process(data);
};
// Component
const ModuleListMain = () => {
    const dispatch = useDispatch();
    const allModules = useSelector(selectAllModules);
    const loading = useSelector(selectModulesLoading);
    const [modules, setModules] = useState([]);
    const [searchText, setSearchText] = useState("");
    const [page, setPage] = useState(MODULE_PAGINATION.DEFAULT_PAGE);
    // drawer
    const [drawerOpen, setDrawerOpen] = useState(false);
    const [editingModule, setEditingModule] = useState(null);
    // table features
    const [sortConfig, setSortConfig] = useState({ columnId: null, direction: "asc" });
    const [hiddenColumns, setHiddenColumns] = useState(new Set());
    const [columnVisibilityAnchor, setColumnVisibilityAnchor] = useState(null);
    const [columnFilters, setColumnFilters] = useState({});
    const [columnOrder, setColumnOrder] = useState(null);
    const [showFilterRow, setShowFilterRow] = useState(false);
    const [activeFilterColumnId, setActiveFilterColumnId] = useState(null);
    const [pinnedColumns, setPinnedColumns] = useState({ left: [], right: [] });
    const isFirstLoad = useRef(true);
    const totalPages = Math.ceil(modules.length / MODULE_PAGINATION.LIMIT);
    const paginatedRows = useMemo(() => {
        const start = (page - 1) * MODULE_PAGINATION.LIMIT;
        return modules.slice(start, start + MODULE_PAGINATION.LIMIT);
    }, [modules, page]);
    // Initial fetch
    useEffect(() => {
        dispatch(fetchModules()).finally(() => {
            isFirstLoad.current = false;
        });
    }, [dispatch]);
    // Re-apply filter sort whenever data or filter state changes
    useEffect(() => {
        let result = applyClientSideOperations(allModules, sortConfig, searchText);
        if (Object.keys(columnFilters).length > 0) {
            const filterRows = (rows) => rows
                .map(row => {
                const children = row.sub_modules?.length ? filterRows(row.sub_modules) : [];
                const selfMatches = Object.entries(columnFilters).every(([col, val]) => {
                    if (!val)
                        return true;
                    const cell = row[col]?.toString().toLowerCase() ?? "";
                    return cell.includes(val.toLowerCase());
                });
                return selfMatches || children.length > 0
                    ? { ...row, sub_modules: children }
                    : null;
            })
                .filter(Boolean);
            result = filterRows(result);
        }
        setModules(result);
        setPage(MODULE_PAGINATION.DEFAULT_PAGE);
    }, [allModules, sortConfig, searchText, columnFilters]);
    // handlers
    const handleSortChange = useCallback((columnId, direction) => setSortConfig({ columnId, direction }), []);
    const handleAddModule = useCallback(() => {
        setEditingModule(null);
        setDrawerOpen(true);
    }, []);
    const handleEditModule = useCallback((module) => {
        setEditingModule(module);
        setDrawerOpen(true);
    }, []);
    const handleDrawerClose = useCallback(() => {
        setDrawerOpen(false);
        setEditingModule(null);
    }, []);
    const handleSaveModule = useCallback(async (payload) => {
        let result;
        if (editingModule) {
            result = await dispatch(updateModule({ editingModule, payload }));
        }
        else {
            result = await dispatch(createModule({ payload }));
        }
        if (result.meta.requestStatus === "fulfilled") {
            setDrawerOpen(false);
            setEditingModule(null);
        }
    }, [dispatch, editingModule]);
    const handleDeleteModule = useCallback((module) => {
        dispatch(deleteModule({ module }));
    }, [dispatch]);
    const handleColumnFilterChange = useCallback((columnId, value) => {
        setColumnFilters(prev => {
            const next = { ...prev };
            if (value)
                next[columnId] = value;
            else
                delete next[columnId];
            return next;
        });
    }, []);
    const handleFilterRowToggle = useCallback((payload = null) => {
        if (!payload || payload?.mode === "all") {
            setActiveFilterColumnId(null);
            setShowFilterRow(prev => payload?.open !== undefined ? payload.open : !prev);
            return;
        }
        if (payload?.mode === "single" && payload?.columnId) {
            if (payload?.open === false) {
                setActiveFilterColumnId(null);
                setShowFilterRow(false);
                return;
            }
            setActiveFilterColumnId(payload.columnId);
            setShowFilterRow(true);
        }
    }, []);
    return (_jsxs(PageContainer, { children: [_jsx(Box, { className: "module-list-container", children: _jsxs(Paper, { className: "module-list-card", elevation: 0, children: [_jsx(ModuleListHeader, { searchText: searchText, setSearchText: setSearchText, showFilterRow: showFilterRow, onFilterRowToggle: () => {
                                setShowFilterRow(prev => {
                                    const next = !prev;
                                    if (!next)
                                        setActiveFilterColumnId(null);
                                    return next;
                                });
                            }, onColumnVisibilityClick: (e) => setColumnVisibilityAnchor(e.currentTarget), onAddModule: handleAddModule }), _jsxs(Box, { className: "module-list-table-wrapper", children: [_jsx(ModuleMap, { modules: paginatedRows, allModules: allModules, loading: loading, isFirstLoad: isFirstLoad.current, sortConfig: sortConfig, onSortChange: handleSortChange, hiddenColumns: hiddenColumns, onColumnVisibilityChange: setHiddenColumns, columnVisibilityAnchor: columnVisibilityAnchor, onColumnVisibilityClose: () => setColumnVisibilityAnchor(null), columnFilters: columnFilters, onColumnFilterChange: handleColumnFilterChange, showFilterRow: showFilterRow, activeFilterColumnId: activeFilterColumnId, onActiveFilterColumnIdChange: setActiveFilterColumnId, onFilterRowToggle: handleFilterRowToggle, columnOrder: columnOrder, onColumnOrderChange: setColumnOrder, pinnedColumns: pinnedColumns, onColumnPinningChange: setPinnedColumns, onEditModule: handleEditModule, onDeleteModule: handleDeleteModule }), modules.length > MODULE_PAGINATION.LIMIT && (_jsx(Box, { className: "module-list-pagination-wrapper", children: _jsx(Pagination, { currentPage: page, totalPages: totalPages, onPageChange: setPage, maxVisiblePages: 5, totalEntries: modules.length, itemsPerPage: MODULE_PAGINATION.LIMIT, showEntriesMessage: true }) }))] })] }) }), _jsx(AddEditModuleDrawer, { open: drawerOpen, onClose: handleDrawerClose, mode: editingModule ? "edit" : "create", moduleData: editingModule, parentModule: null, allModules: allModules, onSave: handleSaveModule })] }));
};
export default ModuleListMain;
